import React, { Fragment, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTable } from 'chameleon/ui-stack/ui/base/table';
import PracticeSkillsPopOver from 'chameleon-packages/units/components/PracticeSkills';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import SubunitsListNumberDisplay from './components/SubunitsListNumberDisplay';
import VisibleUnitsWrapper from './components/VisibleUnitsWrapper';
import Expander from './components/Expander';
import RowWrapper from './components/RowWrapper';
import './styles.css';

const SubunitsList = (props) => {
  const {
    unit,
    courseOutlineConfig: { widgetTagColumn, widgetTagColumnLabel, unitsLabel },
    userData,
    subunitLabel,
    showStudentPracticeProblems,
    disablePpcAutoAssign,
    teacherAssignments,
    subjectId,
  } = props;
  const {
    me: { initId: userId },
    isStudent,
  } = userData;
  const { subunits, number: unitNumber } = unit;

  const subunitRefs = useRef([]);

  const data = useMemo(() => {
    let { subunits: _subunits } = unit;

    if (Number(unitNumber) <= 0) {
      _subunits = _subunits.filter(({ resources }) => resources.length > 0);
    } else if (disablePpcAutoAssign) {
      _subunits = _subunits.filter(({ type, resources }) =>
        type === 'PPC' ? resources.length > 0 : true,
      );
    }

    return _subunits.map((item) => ({
      ...item,
      id: item.number,
      unitNumber,
    }));
  }, [unitNumber, disablePpcAutoAssign]);

  const headers = [
    {
      accessor: 'number',
      Cell: (item) => {
        const { row } = item;
        const {
          iconName,
          displayName,
          unitNumber: currentUnitNumber,
          displayNumber,
        } = row.original;
        const isTeacherResource = displayName === 'Teacher Resources';
        const isStudentResource = displayName === 'Student Resources';
        const isChiefReaderReports = displayName === 'Chief Reader Reports';
        const isCourseLevelResources = displayName === 'Course-Level Resources';
        const isPlanningTools = displayName === 'Planning Tools';
        const isCrosswalks = displayName === 'Crosswalks';
        const isResourceSubunit =
          currentUnitNumber === '0' && iconName === 'Resources';

        if (displayNumber && Number(currentUnitNumber) > 0) {
          return (
            <SubunitsListNumberDisplay
              unitNumber={currentUnitNumber}
              displayNumber={displayNumber}
            />
          );
        } else if (isResourceSubunit && isTeacherResource) {
          return <Icon id="teacher_resources" />;
        } else if (isResourceSubunit && isStudentResource) {
          return <Icon id="student_resources" />;
        } else if (isResourceSubunit && isChiefReaderReports) {
          return <Icon id="chief_reader_reports" />;
        } else if (isResourceSubunit && isCourseLevelResources) {
          return <Icon id="course_level_resources" />;
        } else if (isResourceSubunit && isPlanningTools) {
          return <Icon id="planning_tools" />;
        } else if (isResourceSubunit && isCrosswalks) {
          return <Icon id="crosswalks" />;
        }
        return null;
      },
    },
    {
      accessor: 'displayName',
      className: 'w-full text-left',
      Cell: (item) => {
        const { value } = item;
        const { row } = item;
        const { description } = row.original;
        if (description) {
          return (
            <>
              <div className="ml-12">{value}</div>
              <div className="ml-12 mt-2 text-14">{description}</div>
            </>
          );
        }
        return <div className="ml-12">{value}</div>;
      },
    },
    {
      accessor: 'metadata',
      Cell: ({ value }) => (
        <PracticeSkillsPopOver
          className="mobile:w-40 desktop:w-96"
          skillsContainerClassName="justify-end"
          widgetTagColumn={widgetTagColumn}
          widgetTagColumnLabel={widgetTagColumnLabel}
          metadata={value}
        />
      ),
    },
    {
      Header: () => null,
      id: 'expander',
      Cell: (value) => {
        const {
          row: { original },
        } = value;
        const { number, subunitId, unitNumber } = original;
        const expanderId = number || subunitId;

        // if the unit have no resources, there is nothing to expand to
        if (original.resources?.length === 0) {
          return <div className="h-12 w-12"></div>;
        }
        return (
          <Expander
            expanderId={expanderId}
            unitNumber={unitNumber}
            subjectId={subjectId}
            userId={userId}
            subunitId={subunitId}
          />
        );
      },
    },
  ];

  const { Table, TableBody, TableRow, TableColumns, tableItems } = useTable({
    data,
    headers,
  });

  return (
    <Fragment>
      <VisibleUnitsWrapper
        subjectId={subjectId}
        userId={userId}
        unitNumber={unitNumber}
        subunits={subunits}
        subunitRefs={subunitRefs}
        isStudent={isStudent}
      />
      <Table className="subunits-list">
        <TableBody>
          {tableItems.map((item) => {
            const {
              original: { type, subunitId, resources },
            } = item;
            const isPPC = type === 'PPC';
            const isPE = type === 'PE';
            const isSPP = type === 'SPP';
            const isLC = type === 'LC';
            const isPT = type === 'PT';
            const hasResources = resources && resources.length > 0;

            if (!showStudentPracticeProblems && isSPP) {
              return null;
            }

            //FY-23754: PE assessment for student if there is no resource then hide it the subunit row
            if (isPE && isStudent && !hasResources) {
              return null;
            }

            return (
              <RowWrapper
                key={`fragment-${subunitId}`}
                TableRow={TableRow}
                TableColumns={TableColumns}
                subunitId={subunitId}
                unitNumber={unitNumber}
                subjectId={subjectId}
                userId={userId}
                item={item}
                unitData={unit}
                subunitRefs={subunitRefs}
                isPPC={isPPC}
                isPE={isPE}
                isSPP={isSPP}
                isLC={isLC}
                isPT={isPT}
                widgetTagColumn={widgetTagColumn}
                unitsLabel={unitsLabel}
                subunitLabel={subunitLabel}
                hasResources={hasResources}
                teacherAssignments={teacherAssignments}
              />
            );
          })}
        </TableBody>
      </Table>
    </Fragment>
  );
};

SubunitsList.propTypes = {
  unit: PropTypes.object.isRequired,
  courseOutlineConfig: PropTypes.shape({
    widgetTagColumn: PropTypes.string,
    widgetTagColumnLabel: PropTypes.string,
  }).isRequired,
  subunitLabel: PropTypes.string.isRequired,
};

const mapStateToProps = ({
  impersonating,
  visitedUnits,
  userData,
  featureFlag,
}) => {
  const {
    student_practice_problems,
    spp_filter_by_subject,
    spp_filter_by_school,
  } = featureFlag;

  return {
    impersonating,
    visitedUnits,
    userData,
    showStudentPracticeProblems:
      student_practice_problems ||
      (spp_filter_by_subject && spp_filter_by_school),
    disablePpcAutoAssign: featureFlag.disable_ppc_auto_assign,
  };
};

export default connect(mapStateToProps)(SubunitsList);
